/**
 * API Service for Y GO Shuttle Services France
 */
import axios from 'axios';
import config from 'config';

const API_URL = config.API_URL;
const API = `${API_URL}/api`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── Proactive token refresh ────────────────────────────────────────
// Checks every 5 min if the access token expires within 10 min.
// If so, silently refreshes it BEFORE any 401 occurs.
const PROACTIVE_CHECK_INTERVAL = 5 * 60 * 1000;  // 5 minutes
const PROACTIVE_REFRESH_MARGIN = 10 * 60;         // 10 minutes (in seconds)

function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp || 0;
  } catch {
    return 0;
  }
}

async function proactiveRefresh() {
  const accessToken = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');
  if (!accessToken || !refreshToken) return;

  const now = Math.floor(Date.now() / 1000);
  const expiry = getTokenExpiry(accessToken);

  // If token expires within the margin, refresh it
  if (expiry > 0 && expiry - now < PROACTIVE_REFRESH_MARGIN) {
    try {
      const resp = await axios.post(`${API}/auth/refresh`, {
        refresh_token: refreshToken,
      });
      const { access_token, refresh_token: newRefresh } = resp.data;
      localStorage.setItem('access_token', access_token);
      if (newRefresh) localStorage.setItem('refresh_token', newRefresh);
    } catch {
      // Refresh failed (refresh token expired = 3h00 AM) → will redirect on next 401
    }
  }
}

// Start proactive refresh timer
let _proactiveTimer = null;
function startProactiveRefresh() {
  if (_proactiveTimer) clearInterval(_proactiveTimer);
  _proactiveTimer = setInterval(proactiveRefresh, PROACTIVE_CHECK_INTERVAL);
  // Also do an immediate check
  proactiveRefresh();
}
startProactiveRefresh();

// ─── Reactive 401 handler (fallback) ─────────────────────────────────
let isRefreshing = false;
let refreshQueue = [];

const PUBLIC_PATHS = ['/', '/login', '/register', '/contact', '/prestations', '/transferts', '/entreprises', '/flotte', '/croisieres-excursions', '/booking', '/suivi', '/tracking', '/mentions-legales', '/politique-confidentialite', '/cgv', '/driver/register', '/driver/verify', '/partner/register', '/register/pro', '/forgot-password', '/reset-password', '/app/login', '/delete-account', '/confirm-deletion'];

function isPublicPage() {
  const p = window.location.pathname;
  return PUBLIC_PATHS.some(path => p === path) ||
    p.startsWith('/excursions/') ||
    p.startsWith('/booking/') ||
    p.startsWith('/app');
}

function clearAdminAuth() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalReq = error.config;

    if (error.response?.status === 401 && !originalReq._retry) {
      const refreshToken = localStorage.getItem('refresh_token');

      if (refreshToken) {
        // If another refresh is already in progress, queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            refreshQueue.push({ resolve, reject });
          }).then((token) => {
            originalReq.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalReq);
          });
        }

        originalReq._retry = true;
        isRefreshing = true;

        try {
          // Use raw axios to avoid interceptor loop
          const resp = await axios.post(`${API}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          const { access_token, refresh_token: newRefresh } = resp.data;
          localStorage.setItem('access_token', access_token);
          if (newRefresh) localStorage.setItem('refresh_token', newRefresh);

          // Replay queued requests
          refreshQueue.forEach((q) => q.resolve(access_token));
          refreshQueue = [];
          isRefreshing = false;

          originalReq.headers.Authorization = `Bearer ${access_token}`;
          return apiClient(originalReq);
        } catch {
          refreshQueue.forEach((q) => q.reject(error));
          refreshQueue = [];
          isRefreshing = false;
          // Refresh failed — fall through to logout
        }
      }

      // No refresh token or refresh failed — clear auth and redirect
      clearAdminAuth();
      if (!isPublicPage()) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email, password) => {
    const response = await apiClient.post('/auth/login', { email, password });
    return response.data;
  },

  register: async (userData) => {
    const response = await apiClient.post('/auth/register', userData);
    return response.data;
  },

  logout: async () => {
    try {
      await apiClient.post('/auth/logout');
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
  },

  getMe: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  refreshToken: async (refreshToken) => {
    const response = await apiClient.post('/auth/refresh', { refresh_token: refreshToken });
    return response.data;
  },
};

// Health API
export const healthAPI = {
  check: async () => {
    const response = await apiClient.get('/health');
    return response.data;
  },
};

export default apiClient;
